import CloseIcon from '@mui/icons-material/Close';
import { Box, Grid, IconButton, Modal, Stack, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { endpoint } from '..';
import Button from '../../../components/Button/Button';
import Dialog from '../../../components/Dialog/Dialog';
import { IDropdownOption } from '../../../components/Dropdown/IDropdown';
import { useAppDispatch, useAppSelector } from '../../../reduxService/hooks';
import * as BeMaccaferriStoreHelper from '../../api/store/BeMaccaferriStoreHelper';
import Layout from '../../components/Layout/Layout';
import { EProjectType } from '../../enum/project';
import Macro1Icon from '../../images/secured_drapery.png';
import Macro2Icon from '../../images/simple_drapery.png';
import { commitProject as commitProjectM1, dataToProject as dataToProjectM1, cleanProject as macro1CleanProject, setProject as setProjectM1 } from '../../Macro1/store/project/projectslice';
import { commitProject as commitProjectM2, dataToProject as dataToProjectM2, cleanProject as macro2CleanProject, setProject as setProjectM2 } from '../../Macro2/store/project/projectslice';
import { selectProjectInfo, setProjectInfo } from '../../store/projectInfo/projectInfoSlice';
import { IProjectInfoForm } from '../ProjectInfo/IProjectInfo';
import './ProjectType.scss';
import UnitOfMeasureLogo from './UnitOfMeasureLogo.svg';

const style = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: 'white',
    padding: 20,
    width: 400,
};

const ProjectType = () => {
    const { t } = useTranslation();
    const projectInfo = useAppSelector(selectProjectInfo);
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const [processing, setProcessing] = useState(false);

    const [projectType, setProjectType] = React.useState<string>(projectInfo.data?.projectType ?? EProjectType.Macro1);
    const [loadingProject, setLoadingProject] = React.useState<boolean>(true);
    const [openModal, setOpenModal] = React.useState(false);

    const [ErrorDialog, setErrorDialog] = React.useState<string>('');

    const fetchProject = async (id: string, macro2: boolean) => {
        if (macro2) {
            const res = await BeMaccaferriStoreHelper.getMacro2ProjectAsync(id);
            if (res.data.projectInfos) {
                dispatch(setProjectInfo({ projectId: id, ...res.data.projectInfos, hasBeenSent: false }));
            }
            dispatch(setProjectM2(dataToProjectM2(res.data)));
            dispatch(commitProjectM2(dataToProjectM2(res.data)));
            setProjectType(EProjectType.Macro2);
        } else {
            const res = await BeMaccaferriStoreHelper.getMacro1ProjectAsync(id);
            if (res.data.projectInfos) {
                dispatch(setProjectInfo({ projectId: id, ...res.data.projectInfos, hasBeenSent: false }));
            }
            dispatch(setProjectM1(dataToProjectM1(res.data)));
            dispatch(commitProjectM1(dataToProjectM1(res.data)));
            setProjectType(EProjectType.Macro1);
        }
        setLoadingProject(false);
    };

    const [searchParams] = useSearchParams();

    useEffect(() => {
        if (!!searchParams.get('id')) {
            fetchProject(searchParams.get('id') ?? '', searchParams.get('macro') == '2');
        } else {
            setLoadingProject(false);
        }
    }, []);

    const backToProjectInfo = () => {
        navigate(endpoint.projectInfo);
    };

    const onClickMacro1 = (e: React.MouseEvent<HTMLButtonElement>) => {
        setProjectType(EProjectType.Macro1);
    };

    const onClickMacro2 = (e: React.MouseEvent<HTMLButtonElement>) => {
        setProjectType(EProjectType.Macro2);
    };

    const createProject = async (projectData: IProjectInfoForm) => {
        try {
            let res = await BeMaccaferriStoreHelper.addProjectAsync(projectData);
            let projectId = res.data.projectId;
            navigate(`/designCondition/${projectType}/${projectId}`);
        } catch (ex: any) {
            console.error(ex);
            setErrorDialog('' + t('errors.projectCreate.message'));
        }
    };

    const handleCloseModal = () => setOpenModal(false);

    const handleClickNext = () => {
        if (!projectInfo.initialState) {
            navigate(`/designCondition/${projectInfo.data?.projectType}/${projectInfo.data?.projectId}`);
            return;
        } else {
            setOpenModal(true);
        }
    };

    const handleClick = async (unitOfMeasure: string) => {
        setProcessing(true);

        const handleProjectTypeSelection = () => {
            dispatch(macro2CleanProject());
            dispatch(macro1CleanProject());
            setProjectType(projectType);
            let reqObj = { ...projectInfo.data!, projectType: projectType, unit: unitOfMeasure };
            dispatch(setProjectInfo(reqObj));
            createProject(reqObj);
        };

        handleProjectTypeSelection();
    };

    return (
        <Layout>
            <Dialog
                open={!!ErrorDialog && ErrorDialog.length > 0}
                title={`${t('errors.projectCreate.title')}`}
                onClose={backToProjectInfo}
                actions={[
                    {
                        text: 'ok',
                        callback: backToProjectInfo,
                        color: 'primary',
                    },
                ]}>
                <>
                    <div>{`${ErrorDialog}`}</div>
                </>
            </Dialog>
            {!loadingProject && (
                <>
                    <div className='d-flex justify-content-between w-100 full-height-wrapper projectType-container'>
                        <Grid container style={{ paddingTop: '80px', alignItems: 'center' }} spacing={2}>
                            <Grid container item spacing={2}>
                                {!projectInfo.initialState && (
                                    <Grid item xs={12}>
                                        <p className='error-color text-center' style={{ border: 'none', marginBottom: 0 }}>
                                            {t('project_type_unchangeable')}
                                        </p>
                                    </Grid>
                                )}
                                <Grid item xs={6}>
                                    <Button
                                        classNameBtn={`d-flex flex-column align-items-center gap-3 ${projectType === EProjectType.Macro1 ? 'btn-projectType-active' : 'btn-projectType'}`}
                                        onClick={onClickMacro1}
                                        disabled={!projectInfo.initialState && projectInfo.data?.projectType != EProjectType.Macro1}>
                                        <img src={Macro1Icon} alt='Macr01 Icon' />
                                        <h3>{t('project_type_macro1_title_button')}</h3>
                                        <p>{t('project_type_macro1_description_button')}</p>
                                    </Button>
                                </Grid>
                                <Grid item xs={6}>
                                    <div className='d-flex flex-column'>
                                        <Button
                                            classNameBtn={`d-flex flex-column align-items-center gap-3 ${projectType === EProjectType.Macro2 ? 'btn-projectType-active' : 'btn-projectType'}`}
                                            onClick={onClickMacro2}
                                            disabled={!projectInfo.initialState && projectInfo.data?.projectType != EProjectType.Macro2}>
                                            <img src={Macro2Icon} alt='Macro2 Icon' />
                                            <h3>{t('project_type_macro2_title_button')}</h3>
                                            <p>{t('project_type_macro2_description_button')}</p>
                                        </Button>
                                    </div>

                                    <div className='d-flex flex-column'>
                                        <Button disabled={processing} className='d-flex justify-content-end mt-1' onClick={handleClickNext}>
                                            {!projectInfo.initialState ? t('project_info_go_to_design_condition') : t('next_button')}
                                        </Button>
                                    </div>
                                </Grid>
                            </Grid>
                        </Grid>
                    </div>
                    <Modal open={openModal} onClose={handleCloseModal} aria-labelledby='modal-soil-title' aria-describedby='modal-soil-description'>
                        <Box mx={style}>
                            <Stack style={{ minHeight: '2rem' }} height='100%' direction='column' spacing={3} justifyContent={'center'} alignItems={'center'}>
                                <Stack alignSelf={'end'}>
                                    <IconButton aria-label='close-modal' onClick={handleCloseModal}>
                                        <CloseIcon />
                                    </IconButton>
                                </Stack>
                                <img width={'100px'} src={UnitOfMeasureLogo} alt='UnitOfMeasureLogo' />
                                <Typography variant='h6'>{t('unit_system_title')}</Typography>
                                <Stack direction={'row'} spacing={2}>
                                    <Button disabled={processing} onClick={() => handleClick('metric')}>
                                        {t('metric_unit_system')}
                                    </Button>
                                    <Button disabled={processing} onClick={() => handleClick('imperial')}>
                                        {t('imperial_unit_system')}
                                    </Button>
                                </Stack>
                            </Stack>
                        </Box>
                    </Modal>
                </>
            )}
        </Layout>
    );
};

export default ProjectType;
